/**
 * TriviaBoard Component
 *
 * Displays the current question with timer, answer options, score, and
 * progress indicator. Handles user interaction for selecting answers.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TriviaGameState, TriviaQuestion, TriviaPlayer, TriviaAnswer } from '../types';
import './TriviaBoard.css';

export interface TriviaBoardProps {
  gameState: TriviaGameState;
  currentPlayerId: string;
  onAnswer: (selectedOption: string) => void;
  onTimeUp: () => void;
  onNextQuestion: () => void;
  isAIThinking?: boolean;
  lastAnswer?: TriviaAnswer | null;
}

export const TriviaBoard: React.FC<TriviaBoardProps> = ({
  gameState,
  currentPlayerId,
  onAnswer,
  onTimeUp,
  onNextQuestion,
  isAIThinking = false,
  lastAnswer = null,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(gameState.timePerQuestion);
  const [hasAnswered, setHasAnswered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const timeLeftRef = useRef(timeLeft);

  // Reset state when question changes
  useEffect(() => {
    setSelectedOption(null);
    setHasAnswered(false);
    setTimeLeft(gameState.timePerQuestion);
    timeLeftRef.current = gameState.timePerQuestion;
  }, [gameState.currentQuestion?.id, gameState.timePerQuestion]);

  // Timer logic
  useEffect(() => {
    if (gameState.phase !== 'question' || hasAnswered) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      timeLeftRef.current -= 0.1;
      const newTime = Math.max(0, timeLeftRef.current);
      setTimeLeft(newTime);

      if (newTime <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        onTimeUp();
      }
    }, 100);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState.phase, hasAnswered, onTimeUp]);

  const handleOptionClick = useCallback(
    (option: string) => {
      if (hasAnswered || gameState.phase !== 'question') return;
      setSelectedOption(option);
      setHasAnswered(true);
      onAnswer(option);
    },
    [hasAnswered, gameState.phase, onAnswer]
  );

  const getOptionClass = (option: string): string => {
    const base = 'trivia-board__option';
    if (gameState.phase === 'answer-reveal' || (hasAnswered && gameState.currentQuestion)) {
      if (option === gameState.currentQuestion?.correctAnswer) {
        return `${base} ${base}--correct`;
      }
      if (option === selectedOption && option !== gameState.currentQuestion?.correctAnswer) {
        return `${base} ${base}--incorrect`;
      }
      return `${base} ${base}--dimmed`;
    }
    if (option === selectedOption) {
      return `${base} ${base}--selected`;
    }
    return base;
  };

  const getTimerClass = (): string => {
    if (timeLeft <= 3) return 'trivia-board__timer trivia-board__timer--critical';
    if (timeLeft <= 7) return 'trivia-board__timer trivia-board__timer--warning';
    return 'trivia-board__timer';
  };

  const getTimerPercentage = (): number => {
    return (timeLeft / gameState.timePerQuestion) * 100;
  };

  const currentPlayer = gameState.players.find(p => p.id === currentPlayerId);
  const questionNumber = gameState.currentRound
    ? gameState.currentRound.currentQuestionIndex + 1
    : 0;
  const totalQuestions = gameState.currentRound
    ? gameState.currentRound.questions.length
    : gameState.questionsPerRound;

  if (!gameState.currentQuestion) {
    return (
      <div className="trivia-board trivia-board--empty">
        <p>Waiting for question...</p>
      </div>
    );
  }

  return (
    <div className="trivia-board">
      {/* Header: Timer + Score + Progress */}
      <div className="trivia-board__header">
        <div className="trivia-board__progress">
          <span className="trivia-board__question-count">
            Q {questionNumber}/{totalQuestions}
          </span>
          <span className="trivia-board__category-badge">
            {gameState.currentQuestion.category}
          </span>
        </div>

        <div className={getTimerClass()}>
          <div
            className="trivia-board__timer-bar"
            style={{ width: `${getTimerPercentage()}%` }}
          />
          <span className="trivia-board__timer-text">
            {Math.ceil(timeLeft)}s
          </span>
        </div>

        <div className="trivia-board__scores">
          {gameState.players.map(player => (
            <div
              key={player.id}
              className={`trivia-board__player-score ${
                player.id === currentPlayerId
                  ? 'trivia-board__player-score--current'
                  : ''
              }`}
            >
              <span className="trivia-board__player-name">
                {player.isAI ? '🤖' : '👤'} {player.name}
              </span>
              <span className="trivia-board__score-value">{player.score}</span>
              {player.streak > 1 && (
                <span className="trivia-board__streak">🔥{player.streak}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Question */}
      <div className="trivia-board__question">
        <h2 className="trivia-board__question-text">
          {gameState.currentQuestion.question}
        </h2>
        {gameState.currentQuestion.type === 'true-false' && (
          <span className="trivia-board__type-badge">True / False</span>
        )}
      </div>

      {/* Options */}
      <div className="trivia-board__options">
        {gameState.currentQuestion.options.map((option, index) => (
          <button
            key={`${gameState.currentQuestion!.id}-${index}`}
            className={getOptionClass(option)}
            onClick={() => handleOptionClick(option)}
            disabled={hasAnswered || gameState.phase !== 'question'}
            aria-label={`Option ${index + 1}: ${option}`}
          >
            <span className="trivia-board__option-letter">
              {String.fromCharCode(65 + index)}
            </span>
            <span className="trivia-board__option-text">{option}</span>
            {(gameState.phase === 'answer-reveal' || hasAnswered) &&
              option === gameState.currentQuestion?.correctAnswer && (
                <span className="trivia-board__option-icon">✓</span>
              )}
            {(gameState.phase === 'answer-reveal' || hasAnswered) &&
              option === selectedOption &&
              option !== gameState.currentQuestion?.correctAnswer && (
                <span className="trivia-board__option-icon">✗</span>
              )}
          </button>
        ))}
      </div>

      {/* Answer Feedback */}
      {(gameState.phase === 'answer-reveal' || hasAnswered) && lastAnswer && (
        <div
          className={`trivia-board__feedback ${
            lastAnswer.isCorrect
              ? 'trivia-board__feedback--correct'
              : 'trivia-board__feedback--incorrect'
          }`}
        >
          <p className="trivia-board__feedback-text">
            {lastAnswer.isCorrect
              ? `Correct! +${lastAnswer.pointsEarned} points`
              : `Incorrect! The answer was: ${gameState.currentQuestion?.correctAnswer}`}
          </p>
          {gameState.currentQuestion?.explanation && (
            <p className="trivia-board__explanation">
              {gameState.currentQuestion.explanation}
            </p>
          )}
        </div>
      )}

      {/* Time Up Feedback */}
      {gameState.phase === 'answer-reveal' && !hasAnswered && (
        <div className="trivia-board__feedback trivia-board__feedback--timeout">
          <p className="trivia-board__feedback-text">
            Time is up! The answer was: {gameState.currentQuestion?.correctAnswer}
          </p>
          {gameState.currentQuestion?.explanation && (
            <p className="trivia-board__explanation">
              {gameState.currentQuestion.explanation}
            </p>
          )}
        </div>
      )}

      {/* AI Thinking Indicator */}
      {isAIThinking && (
        <div className="trivia-board__ai-thinking">
          <span className="trivia-board__ai-dots">🤖 AI is thinking</span>
        </div>
      )}

      {/* Next Question Button */}
      {(gameState.phase === 'answer-reveal' || (hasAnswered && !isAIThinking)) && (
        <div className="trivia-board__actions">
          <button
            className="trivia-board__next-btn"
            onClick={onNextQuestion}
            aria-label="Next question"
          >
            Next Question →
          </button>
        </div>
      )}
    </div>
  );
};

export default TriviaBoard;
